import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { memo, useContext, useEffect, useState } from 'react';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { PageContext } from '../../../contexts/PageContext';
import { QuoteContext } from '../../../contexts/Quote';
import { useGetCostBreakdown } from '../../../hooks/useGetCostBreakdown';
import { useGetQuote } from '../../../hooks/useGetQuote';
import { Blok } from '../../../interfaces/Blok.interface';
import { LinkProps } from '../../../interfaces/LinkProps.interface';
import { postQuoteOverviewToDataLayer } from '../../../utils/postQuoteOverviewToDataLayer/postQuoteOverviewToDataLayer.util';
import QuoteFormTabBar from '../../QuoteFormTabBar/QuoteFormTabBar';
import TestingGrid from '../../TestingGrid/TestingGrid.molecule';
import QuoteStageMainLayout from '../QuoteStageMainLayout/QuoteStageMainLayout';
import QuoteStageTwoPageLoadingState from './QuoteStageTwoPageLoadingState';

interface QuoteStageTwoPageBlokProps extends Blok {
  stage_one_tab_link: LinkProps[];
  stage_two_tab_label: string;
  stage_three_tab_link: LinkProps[];
  heading: string;
  left_column: Blok[];
  right_column: Blok[];
}

interface QuoteStageTwoPageProps {
  blok: QuoteStageTwoPageBlokProps;
}

const QuoteStageTwoPage: React.FC<QuoteStageTwoPageProps> = ({ blok }) => {
  const {
    stage_one_tab_link,
    stage_two_tab_label,
    stage_three_tab_link,
    heading,
    left_column,
    right_column,
  } = blok;
  const { header, footer } = useContext(LayoutContext);
  let searchParams: URLSearchParams = new URLSearchParams();
  const [quoteParams, setQuoteParams] = useState({
    quoteReference: '',
    trackingRef: '',
  });

  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.get('quotation') && !searchParams.get('TrackingRef')) {
      navigate('/instant-quote/quote-stage-one/');
    }

    setQuoteParams({
      quoteReference: searchParams.get('quotation') || '',
      trackingRef: searchParams.get('TrackingRef') || '',
    });
  }, []);

  const [quote, quoteErrors, quoteIsLoading] = useGetQuote(
    quoteParams.quoteReference,
    quoteParams.trackingRef
  );

  const [costBreakdown, costBreakdownErrors, costBreakdownIsLoading] =
    useGetCostBreakdown(quoteParams.quoteReference, quoteParams.trackingRef);

  useEffect(() => {
    if (quote && costBreakdown) {
      setIsLoading(false);
    }
  }, [quote, costBreakdown]);

  useEffect(() => {
    setHasErrors(quoteErrors || costBreakdownErrors);
  }, [quoteErrors, costBreakdownErrors]);

  if (hasErrors) {
    navigate(`/instant-quote/quote-not-found/`);
  }

  const handleSubmit = () => {
    handlePostQuoteOverviewToDataLater('quote_step2_continue');
    navigate(
      `/instant-quote/quote-stage-three/?quotation=${quoteParams.quoteReference}&TrackingRef=${quoteParams.trackingRef}`
    );
  };

  useEffect(() => {
    if (quote && costBreakdown) {
      handlePostQuoteOverviewToDataLater('quote_step2_view');
    }
  }, [quote && costBreakdown]);

  const handlePostQuoteOverviewToDataLater = (eventName: string): void => {
    postQuoteOverviewToDataLayer(
      eventName,
      quoteParams.quoteReference!,
      quoteParams.trackingRef!,
      costBreakdown!
    );
  };

  return (
    <QuoteContext.Provider
      value={{
        quoteDetails: quote,
        quoteCostBreakdown: costBreakdown,
        quoteReference: quoteParams.quoteReference,
        trackingRef: quoteParams.quoteReference,
      }}
    >
      <PageContext.Provider
        value={{
          headerIsTransparent: false,
          isHeaderSearchFunctionalityRequired: true,
          showContactBar: false,
        }}
      >
        <div className="flex flex-col h-screen justify-between">
          {header && (
            <StoryblokComponent blok={header.content} key={header._uid} />
          )}
          {isLoading && (
            <main className="mb-auto">
              <QuoteStageTwoPageLoadingState />
            </main>
          )}
          {quote && costBreakdown && (
            <main className="mb-auto" {...storyblokEditable(blok)}>
              <QuoteFormTabBar
                tabOne={stage_one_tab_link}
                tabTwo={stage_two_tab_label}
                tabThree={stage_three_tab_link}
                activeTabNumber={2}
              />

              <QuoteStageMainLayout
                heading={heading}
                leftColumn={
                  <Formik initialValues={{}} onSubmit={handleSubmit}>
                    <Form className="sm:pb-20">
                      {left_column.map((blok: Blok) => (
                        <StoryblokComponent blok={blok} key={blok._uid} />
                      ))}
                    </Form>
                  </Formik>
                }
                rightColumn={
                  <div className="xl:space-y-8 flex flex-col-reverse xl:flex-col">
                    {right_column.map((blok: Blok) => (
                      <StoryblokComponent blok={blok} key={blok._uid} />
                    ))}
                  </div>
                }
              />
            </main>
          )}
          {footer && (
            <StoryblokComponent blok={footer.content} key={footer._uid} />
          )}
        </div>
        <TestingGrid />
      </PageContext.Provider>
    </QuoteContext.Provider>
  );
};

export default memo(QuoteStageTwoPage);
